import React, { useState, useRef } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles, useTheme } from '@material-ui/styles';
import {
  Button,
  Dialog,
  Typography,
  colors,
  IconButton,
  useMediaQuery,
  Theme,
  TextField,
  Divider,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import EditIcon from '@material-ui/icons/Edit';
import { LoadingRing } from 'components/Loading/Loading';
import {Editor} from 'components'
import repositories from 'services/repositories'
import useSnackbar from 'utils/useSnackbar';
import DatasetDTO from 'dto/DatasetDTO';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    overflow: 'hidden',
  },
  header: {
    position: 'sticky',
    top: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2)
  },
  main: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    overflow: 'auto',
    '& > *:not(:last-child)': {
      marginBottom: theme.spacing(2)
    },
    padding: theme.spacing(1,2),
  },
  divider: {
    margin: theme.spacing(1, 0)
  },
  actions: {
    position: 'sticky',
    bottom: 0,
    backgroundColor: colors.grey[100],
    padding: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between'
  },
  flexRow: {
    display: 'flex',
    alignItems: 'center'
  },
  editor: {
    borderLeft: '1px solid #eee',
    borderRight: '1px solid #eee',
    borderRadius: 4
  }
}));

interface UpdateDatasetModalProps {
  open: boolean,
  onClose: () => void,
  className?: string,
  repositoryID: number
  dataset: DatasetDTO,
  handleRefresh: () => void
}

interface FormState {
  html: string,
  name: string,
  title: string,
  description: string
}

const formStateEqual = (a: FormState, b: FormState) => !!a && !!b && Object.keys(a).every(k => a[k] === b[k]);

const UpdateDatasetModal = (props: UpdateDatasetModalProps) => {
  const { open, onClose, className, repositoryID, dataset, handleRefresh } = props;

  const theme = useTheme() as Theme;
  const classes = useStyles();
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const [loading, setLoading] = useState<boolean>(true);
  const [state, setState] = useState<FormState>({
    description: dataset.description,
    name: dataset.name,
    title: dataset.title,
    html: ''
  });
  const initialState = useRef<FormState>(null);
  const dispatchAlert = useSnackbar();

  const handleClose = () => {
    onClose();
    initialState.current = null;
    setLoading(true);
  };

  const onSubmit = async () => {
    setLoading(true);
    try {
      await repositories.updateDataset({
        repoID: repositoryID,
        datasetID: dataset.id,
        name: state.name === dataset.name ? null:state.name,
        description: state.description === dataset.description ? null:state.description,
        title: state.title === dataset.title ? null:state.title,
        html: state.html === initialState.current.html ? null:state.html,
      });
      handleRefresh();
      handleClose();
    } catch {
      setLoading(false);
    }
  }

  React.useEffect(() => {
    if(dataset.id && open){
      repositories.getDoc(repositoryID, dataset.id).then((html) => {
        initialState.current = {...state, html};
        setState(pv => ({...pv, html}));
        setLoading(false);
      }).catch(() => {
        setLoading(false);
        dispatchAlert({message: 'There was an issue loading the existing document, update at your own risk', noBackdrop: true})
      })
    }
  }, [dataset.id, open, repositoryID]);

  const handleUpload = ({target}) => {
    const { files: [file] } = target;
    const invalid = (file.size / 1024 / 1024) > 20;
    // setInvalidSize(invalid);
    setLoading(true);
    if (invalid) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setState(pv => ({...pv, html: reader.result.toString()}));
      setLoading(false);
    }
    reader.readAsText(file)
  }

  return (
    <Dialog
      classes={{ paper: clsx(classes.root, className) }}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      open={open}
    >
      <div className={classes.header}>
        <Typography
          variant="h3"
        >
          Update Dataset
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <div className={classes.main}>
        <TextField
          label='Name'
          onChange={e => setState(pv => ({...pv, name: e.target.value}))}
          size='small'
          value={state.name}
          variant='outlined'
        />
        <TextField
          label='Title'
          onChange={e => setState(pv => ({...pv, title: e.target.value}))}
          size='small'
          value={state.title}
          variant='outlined'
        />
        <TextField
          label='Comment'
          onChange={e => setState(pv => ({...pv, description: e.target.value}))}
          size='small'
          value={state.description}
          variant='outlined'
        />
        <Divider/>
        <Editor
          className={classes.editor}
          handleUpload={handleUpload}
          onChange={(newHtml) => setState(pv => ({...pv, html: newHtml}))}
          placeholder="Enter documentation..."
          theme="snow"
          value={state.html}
        />
      </div>
      <div className={classes.actions}>
        <Button
          color="primary"
          disabled={formStateEqual(state, initialState.current) || loading}
          endIcon={<EditIcon />}
          onClick={onSubmit}
          variant="contained"
        >
          {loading ? <LoadingRing
            color='white'
          /> :'Update'}
        </Button>
      </div>
    </Dialog>
  );
};

UpdateDatasetModal.propTypes = {
  className: PropTypes.string,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  open: PropTypes.bool
};

export default UpdateDatasetModal;
